import { useEffect, useState } from "react";

/**
 * HeroSequence — cinematic crossfade slideshow.
 *
 * Crossfade fixes applied here:
 *  1. opacity transition is a smooth cubic-bezier(0.4, 0, 0.2, 1) over 1.8s.
 *  2. Container has a near-black background so we never see a flash of the
 *     site palette between slides.
 *  3. Ken-Burns animation runs on ALL slides continuously (looped infinite,
 *     alternating). That way the incoming slide is already mid-motion when it
 *     fades in — never a snap-back to its zero state.
 *  4. No inline `transform` toggling — that fought the CSS animation and
 *     produced the visible jump.
 *  5. Every slide is pre-decoded at mount so the fade never waits on network.
 *
 * Props:
 *  - slides[]: { src, subject?, kenBurns, overlayStrength }
 *  - durationPerSlide (ms): how long between active-slide swaps
 *  - fadeDuration (ms): crossfade duration
 *
 * Respects `prefers-reduced-motion`.
 */
const OVERLAY_OPACITY = { light: 0.08, medium: 0.18, heavy: 0.30 };

export default function HeroSequence({
  slides,
  durationPerSlide = 5000,
  fadeDuration = 1800,
}) {
  const [active, setActive] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Pre-decode every frame at mount so crossfades never stall.
  // Browser will pick WebP if supported, JPEG otherwise — we trigger both via
  // separate Image objects; the unused one is essentially free (HEAD-like).
  useEffect(() => {
    slides.forEach((s) => {
      const img = new Image();
      img.decoding = "async";
      img.src = s.srcWebp || s.src;
    });
  }, [slides]);

  useEffect(() => {
    if (reduced) return undefined;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % slides.length);
    }, durationPerSlide);
    return () => clearInterval(id);
  }, [slides.length, durationPerSlide, reduced]);

  if (reduced) {
    const s = slides[0];
    return (
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "#1a1a1a" }}
        data-testid="hero-sequence-static"
      >
        <picture>
          {s.srcWebp && <source srcSet={s.srcWebp} type="image/webp" />}
          <img
            src={s.src}
            alt={s.subject || ""}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </picture>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundColor: `rgba(15,15,15,${OVERLAY_OPACITY[s.overlayStrength || "medium"]})` }}
          aria-hidden
        />
      </div>
    );
  }

  return (
    <div
      className="absolute inset-0"
      style={{ backgroundColor: "#1a1a1a" }}
      data-testid="hero-sequence"
    >
      {slides.map((s, idx) => {
        const isActive = idx === active;
        const kbClass = `hero-kb-${s.kenBurns || "zoom-in"}-loop`;
        const overlayAlpha = OVERLAY_OPACITY[s.overlayStrength || "medium"];
        return (
          <div
            key={s.src}
            className="absolute inset-0 overflow-hidden"
            style={{
              opacity: isActive ? 1 : 0,
              transition: `opacity ${fadeDuration}ms cubic-bezier(0.4, 0.0, 0.2, 1)`,
              willChange: "opacity",
            }}
            data-testid={`hero-slide-${idx}`}
            aria-hidden={!isActive}
          >
            {/* Ken-Burns animation runs CONTINUOUSLY on every slide regardless
                of opacity. That way the incoming slide is already in motion. */}
            <picture>
              {s.srcWebp && <source srcSet={s.srcWebp} type="image/webp" />}
              <img
                src={s.src}
                alt={s.subject || ""}
                loading="eager"
                decoding="async"
                fetchPriority={idx <= 1 ? "high" : "auto"}
                className={`absolute inset-0 w-full h-full object-cover ${kbClass}`}
              />
            </picture>
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ backgroundColor: `rgba(15,15,15,${overlayAlpha})` }}
              aria-hidden
            />
          </div>
        );
      })}
    </div>
  );
}
